import * as _ from 'lodash';

import { CreditAgreement, Receivable } from '@finley-technologies/finley-types';

import type { ExpandedConcentrationLimitCalculations } from 'packages/borrowing-base/ui/borrowingBaseSummary/utils';
import { groupMultiLpConcentrationKeysByLoanProducts } from 'packages/borrowing-base/ui/borrowingBaseSummary/utils';

export type CreditFacilityTestStats = {
  totalEligibilityKickout: number;
  eligibilityKickoutByLoanProductId: { [loanProductId: string]: number };
  eligibilityTestsByLoanProductId: {
    [loanProductId: string]: {
      [key: string]: number;
    };
  };
  totalExposureLessEligibilityKickouts: number;
  totalExposureLessEligibilityKickoutsByLoanProductId: {
    [loanProductId: string]: number;
  };
  totalConcentrationKickout: number;
  concentrationKickoutByLoanProductId: { [loanProductId: string]: number };
  concentrationLimitTestsByLoanProductId: {
    [loanProductId: string]: {
      [key: string]: ExpandedConcentrationLimitCalculations | undefined;
    };
  };
  totalEligibleExposureLessConcentrationKickouts: number;
  totalEligibleExposureLessConcentrationKickoutsByLoanProductId: {
    [loanProductId: string]: number;
  };
};

export type PortfolioTestStats = {
  aggregateEligibilityKickouts: number;
  aggregateExposureLessEligibilityKickouts: number;
  aggregateConcentrationKickout: number;
  aggregateEligibleExposureLessConcentrationKickouts: number;
  creditFacilityTestStats: {
    [creditFacilityId: string]: CreditFacilityTestStats;
  };
};

// move any calculations/aggregations in this method into the creation of AssignmentSummary for caching
export const createPortfolioTestStats = ({
  summaryPreview,
  parsedCreditAgreements,
}: {
  summaryPreview: Receivable.ReceivableResponseTapePreviewJobs.AggregatedAssignmentSummaryPreview;
  parsedCreditAgreements: CreditAgreement.DomainEntity.CreditAgreementFullArtifact[];
}): PortfolioTestStats => {
  const portfolioTestStats: PortfolioTestStats = {
    aggregateEligibilityKickouts: 0,
    aggregateExposureLessEligibilityKickouts: 0,
    aggregateConcentrationKickout: 0,
    aggregateEligibleExposureLessConcentrationKickouts: 0,
    creditFacilityTestStats: {},
  };

  parsedCreditAgreements.forEach((creditAgreement) => {
    const {
      borrowingBaseMechanics: {
        calcAgentMechanics: {
          loanProductMechanics,
          multiLoanProductConcentrationLimits,
        },
      },
    } = creditAgreement;

    const multiLpConcentrationKeysByLoanProductsGrouping = groupMultiLpConcentrationKeysByLoanProducts(
      { multiLoanProductConcentrationLimits },
    );

    const {
      totalEligibilityKickoutByLoanProductId,
      totalExposureLessEligibilityKickoutsByLoanProductId,
      eligibilityCriteriaCalculations,
      concentrationLimitCalculations,
      totalEligibleExposureLessConcentrationKickouts,
    } = summaryPreview.borrowingBaseReportMap[creditAgreement.creditFacilityId];

    const eligibilityCriteriaCalculationsMap = new Map<string, number>();
    eligibilityCriteriaCalculations.forEach((calc) => {
      eligibilityCriteriaCalculationsMap.set(
        calc.eligibilityCriteriaKey,
        calc.eligibilityReduction,
      );
    });

    const concentrationLimitCalculationsMap = new Map<
      string,
      CreditAgreement.Config.ConcentrationLimit.Calculation.ConcentrationLimitCalculation
    >();
    concentrationLimitCalculations.forEach((calc) => {
      concentrationLimitCalculationsMap.set(calc.concentrationLimitKey, calc);
    });

    const eligibilityTestsByLoanProductId: {
      [loanProductId: string]: {
        [key: string]: number;
      };
    } = {};
    const concentrationKickoutByLoanProductId: {
      [loanProductId: string]: number;
    } = {};
    const totalEligibleExposureLessConcentrationKickoutsByLoanProductId: {
      [loanProductId: string]: number;
    } = {};
    const concentrationLimitTestsByLoanProductId: {
      [loanProductId: string]: {
        [key: string]: ExpandedConcentrationLimitCalculations | undefined;
      };
    } = {};

    loanProductMechanics.forEach((loanProductMechanic) => {
      const loanProductId = loanProductMechanic.entity.id;
      loanProductMechanic.eligibilityTests.forEach((eligibilityTest) => {
        if (eligibilityTest._config_.eligibilityTestConfig) {
          const eligibilityCriteriaKey =
            eligibilityTest._config_.eligibilityTestConfig.propertyKey;
          const eligibilityReduction =
            eligibilityCriteriaCalculationsMap.get(eligibilityCriteriaKey) ?? 0;
          const loanProductTestsMap =
            eligibilityTestsByLoanProductId[loanProductId] ?? {};
          loanProductTestsMap[eligibilityCriteriaKey] = eligibilityReduction;
          eligibilityTestsByLoanProductId[loanProductId] = loanProductTestsMap;
        }
      });
      loanProductMechanic.concentrationLimits.forEach((concentrationLimit) => {
        if (concentrationLimit._config_.concentrationLimitConfig) {
          const concentrationTestKey =
            concentrationLimit._config_.concentrationLimitConfig.propertyKey;
          const concentrationLimitCalc = concentrationLimitCalculationsMap.get(
            concentrationTestKey,
          );
          const borrowingBaseReduction =
            concentrationLimitCalc?.borrowingBaseReduction ?? 0;
          concentrationKickoutByLoanProductId[loanProductId] =
            (concentrationKickoutByLoanProductId[loanProductId] ?? 0) +
            borrowingBaseReduction;
          const loanProductTestsMap =
            concentrationLimitTestsByLoanProductId[loanProductId] ?? {};
          loanProductTestsMap[concentrationTestKey] = concentrationLimitCalc
            ? {
                ...concentrationLimitCalc,
                concentrationLimitAmount:
                  concentrationLimitCalc.concentrationLimit *
                  concentrationLimitCalc.totalEligibleExposure,
                // TODO(jon): The behavior here matches the legacy behavior, but it is incorrect
                //  because it does not account for waterfall kickouts.
                totalEligibleExposureLessConcentrationKickouts:
                  concentrationLimitCalc.totalEligibleExposure -
                  concentrationLimitCalc.borrowingBaseReduction,
              }
            : concentrationLimitCalc;
          concentrationLimitTestsByLoanProductId[
            loanProductId
          ] = loanProductTestsMap;
        }
      });
    });

    _.map(
      multiLpConcentrationKeysByLoanProductsGrouping,
      (multiLpConcentrationKeys, lpGroupingSlugsString) => {
        multiLpConcentrationKeys.forEach((multiLpConcentrationKey) => {
          const concentrationLimitCalc = concentrationLimitCalculationsMap.get(
            multiLpConcentrationKey,
          );
          const borrowingBaseReduction =
            concentrationLimitCalc?.borrowingBaseReduction ?? 0;
          concentrationKickoutByLoanProductId[lpGroupingSlugsString] =
            (concentrationKickoutByLoanProductId[lpGroupingSlugsString] ?? 0) +
            borrowingBaseReduction;
          const loanProductTestsMap =
            concentrationLimitTestsByLoanProductId[lpGroupingSlugsString] ?? {};
          loanProductTestsMap[multiLpConcentrationKey] = concentrationLimitCalc
            ? {
                ...concentrationLimitCalc,
                concentrationLimitAmount:
                  concentrationLimitCalc.concentrationLimit *
                  concentrationLimitCalc.totalEligibleExposure,
                // TODO(jon): The behavior here matches the legacy behavior, but it is incorrect
                //  because it does not account for waterfall kickouts.
                totalEligibleExposureLessConcentrationKickouts:
                  concentrationLimitCalc.totalEligibleExposure -
                  concentrationLimitCalc.borrowingBaseReduction,
              }
            : concentrationLimitCalc;
          concentrationLimitTestsByLoanProductId[
            lpGroupingSlugsString
          ] = loanProductTestsMap;
        });
      },
    );

    loanProductMechanics.forEach((loanProductMechanic) => {
      const loanProductId = loanProductMechanic.entity.id;
      totalEligibleExposureLessConcentrationKickoutsByLoanProductId[
        loanProductId
      ] =
        totalExposureLessEligibilityKickoutsByLoanProductId[loanProductId] -
        concentrationKickoutByLoanProductId[loanProductId];
    });

    const creditFacilityTestStat: CreditFacilityTestStats = {
      totalEligibilityKickout: _.sum(
        Object.values(totalEligibilityKickoutByLoanProductId),
      ),
      eligibilityKickoutByLoanProductId: totalEligibilityKickoutByLoanProductId,
      totalExposureLessEligibilityKickouts: _.sum(
        Object.values(totalExposureLessEligibilityKickoutsByLoanProductId),
      ),
      totalExposureLessEligibilityKickoutsByLoanProductId,
      eligibilityTestsByLoanProductId,
      totalConcentrationKickout: _.sum(
        _.map(concentrationLimitCalculations, 'borrowingBaseReduction'),
      ),
      concentrationKickoutByLoanProductId,
      concentrationLimitTestsByLoanProductId,
      totalEligibleExposureLessConcentrationKickouts,
      totalEligibleExposureLessConcentrationKickoutsByLoanProductId,
    };

    portfolioTestStats.aggregateConcentrationKickout +=
      creditFacilityTestStat.totalConcentrationKickout;
    portfolioTestStats.aggregateEligibilityKickouts +=
      creditFacilityTestStat.totalEligibilityKickout;
    portfolioTestStats.aggregateEligibleExposureLessConcentrationKickouts +=
      creditFacilityTestStat.totalEligibleExposureLessConcentrationKickouts;
    portfolioTestStats.aggregateExposureLessEligibilityKickouts +=
      creditFacilityTestStat.totalExposureLessEligibilityKickouts;

    portfolioTestStats.creditFacilityTestStats[
      creditAgreement.creditFacilityId
    ] = creditFacilityTestStat;
  });

  return portfolioTestStats;
};
